// The shared CC0 asset library: a content-addressed manifest of GLB models
// (published by sstk-studio-infra's scripts/workspace/asset-library/publish.mjs
// or publish-from-attribution.mjs) registered as placeable objects for every
// project. The host fetches the manifest and calls registerLibraryPack;
// everything here is pure registry work so it stays testable and boots fine
// when the library is absent.
import {
  registerObjects,
  type ObjectDefinition,
} from '../../registry/objectRegistry';
import { getRenderer, registerRenderers, type AssetRenderer } from '../../registry/objectRenderers';

export const LIBRARY_MANIFEST_SCHEMA_VERSION = 1;

/** Registry ids are namespaced so they can never collide with core ids or
 * per-project uploaded asset UUIDs. Placed instances persist this id. */
export const LIBRARY_ID_PREFIX = 'library:';

export interface LibraryAsset {
  id: string;
  name: string;
  kind: string;
  author: string;
  bundle: string;
  license: string;
  /** Blob path relative to the library base URL. */
  file: string;
  sha256: string;
  size: number;
  bounds: { size: [number, number, number]; center: [number, number, number] };
  animations: string[];
  tags: string[];
}

export interface LibraryManifest {
  schemaVersion: number;
  generatedAt: string;
  authors: Record<string, { license: string; url: string }>;
  assets: LibraryAsset[];
}

/** An animated library model, for the Animation Studio's library mode. */
export interface LibraryAnimatedModel {
  assetId: string;
  name: string;
  author: string;
  bundle: string;
  url: string;
  clips: string[];
}

function isVec3(value: unknown): value is [number, number, number] {
  return Array.isArray(value) && value.length === 3 && value.every((n) => typeof n === 'number' && Number.isFinite(n));
}

/**
 * Validate a fetched manifest. Unknown asset kinds are kept (the registrar
 * skips them); malformed entries are dropped rather than crashing the boot.
 */
export function parseLibraryManifest(raw: unknown): LibraryManifest {
  const m = raw as Partial<LibraryManifest> | null;
  if (!m || typeof m !== 'object') throw new Error('Library manifest is not an object.');
  if (m.schemaVersion !== LIBRARY_MANIFEST_SCHEMA_VERSION) {
    throw new Error(`Unsupported library manifest schema ${String(m.schemaVersion)}.`);
  }
  if (!Array.isArray(m.assets)) throw new Error('Library manifest has no assets array.');
  const assets: LibraryAsset[] = [];
  for (const entry of m.assets as Partial<LibraryAsset>[]) {
    if (
      typeof entry?.id !== 'string' || !entry.id ||
      typeof entry.name !== 'string' ||
      typeof entry.kind !== 'string' ||
      typeof entry.file !== 'string' ||
      typeof entry.sha256 !== 'string' ||
      !isVec3(entry.bounds?.size) || !isVec3(entry.bounds?.center)
    ) {
      continue;
    }
    assets.push({
      id: entry.id,
      name: entry.name,
      kind: entry.kind,
      author: typeof entry.author === 'string' ? entry.author : 'Unknown',
      bundle: typeof entry.bundle === 'string' ? entry.bundle : 'Misc',
      license: typeof entry.license === 'string' ? entry.license : 'CC0-1.0',
      file: entry.file,
      sha256: entry.sha256,
      size: typeof entry.size === 'number' ? entry.size : 0,
      bounds: { size: entry.bounds!.size as [number, number, number], center: entry.bounds!.center as [number, number, number] },
      animations: Array.isArray(entry.animations) ? entry.animations.filter((c): c is string => typeof c === 'string') : [],
      tags: Array.isArray(entry.tags) ? entry.tags.filter((t): t is string => typeof t === 'string') : [],
    });
  }
  return {
    schemaVersion: LIBRARY_MANIFEST_SCHEMA_VERSION,
    generatedAt: typeof m.generatedAt === 'string' ? m.generatedAt : '',
    authors: m.authors && typeof m.authors === 'object' ? (m.authors as LibraryManifest['authors']) : {},
    assets,
  };
}

let registeredBase: string | null = null;
const animatedModels: LibraryAnimatedModel[] = [];
const thumbShaByAssetId = new Map<string, string>();
// manifest.authors is keyed by author NAME, not per-asset, so this is a
// small side table (like thumbShaByAssetId above) rather than a field on
// every one of 5k+ definitions.
const authorUrlByName = new Map<string, string>();

/** True once registerLibraryPack has run. */
export function isLibraryRegistered(): boolean {
  return registeredBase !== null;
}

/** Animated library models for the Animation Studio (clips play on their own model). */
export function listLibraryAnimatedModels(): LibraryAnimatedModel[] {
  return animatedModels;
}

/** The content hash naming an asset's baked thumbnail (thumbs/<sha>.png). */
export function libraryThumbSha(assetId: string): string | null {
  return thumbShaByAssetId.get(assetId) ?? null;
}

/** The creator's own site, for attribution display. */
export function libraryAuthorUrl(author: string): string | null {
  return authorUrlByName.get(author) ?? null;
}

function slug(text: string): string {
  return text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}

/**
 * Register every model in the manifest as a placeable object, mirroring the
 * uploaded-asset pattern: the gltf_model renderer wrapped to base-align and
 * XZ-centre the model from its baked bounds. Idempotent; later calls with a
 * different base are ignored (the first registration wins for the session).
 */
export function registerLibraryPack(manifest: LibraryManifest, libraryBase: string): void {
  if (registeredBase !== null) return;
  const base = libraryBase.replace(/\/+$/, '');
  registeredBase = base;

  const gltfRenderer = getRenderer('gltf_model');
  const definitions: ObjectDefinition[] = [];
  const renderers: Record<string, AssetRenderer> = {};

  for (const [author, info] of Object.entries(manifest.authors)) {
    if (info?.url) authorUrlByName.set(author, info.url);
  }

  for (const asset of manifest.assets) {
    // Unknown kinds (future 2D sprites, audio) are skipped, never fatal.
    if (asset.kind !== 'model') continue;
    const id = LIBRARY_ID_PREFIX + asset.id;
    const url = `${base}/${asset.file}`;
    thumbShaByAssetId.set(id, asset.sha256);

    definitions.push({
      id,
      name: asset.name,
      // The manifest carries no category, and library items never take the
      // category-grouping path in either host (ObjectBrowser facets them by
      // author/bundle instead; the category grid is filtered to non-library
      // ids) - this is a placeholder value, not read for library content.
      category: 'structural',
      description: `${asset.author} · ${asset.bundle} (${asset.license})`,
      bounds: { size: asset.bounds.size },
      defaultParams: { url, scale: 1 },
      editor: {
        pivot: 'base-center',
        snap: 0.1,
        tags: ['library', slug(asset.author), slug(asset.bundle), ...(asset.animations.length > 0 ? ['animated'] : []), ...asset.tags],
        thumbnailOverride: `${base}/thumbs/${asset.sha256}.png`,
        author: asset.author,
        bundle: asset.bundle,
      },
    });

    if (gltfRenderer) {
      const [cx, cy, cz] = asset.bounds.center;
      const sizeY = asset.bounds.size[1];
      const baseOffset: [number, number, number] = [-cx, -(cy - sizeY / 2), -cz];
      renderers[id] = (props) => {
        // Library instances always know their own URL: merge the definition
        // defaults under any instance params, so imported or hand-authored
        // maps render without the placement-time param copy.
        const params = { url, scale: 1, ...(props.instance.params ?? {}) };
        const instance = { ...props.instance, params };
        const scale = typeof params.scale === 'number' && Number.isFinite(params.scale) ? params.scale : 1;
        return (
          <group position={[baseOffset[0] * scale, baseOffset[1] * scale, baseOffset[2] * scale]}>
            {gltfRenderer({ ...props, instance })}
          </group>
        );
      };
    }

    if (asset.animations.length > 0) {
      animatedModels.push({
        assetId: id,
        name: asset.name,
        author: asset.author,
        bundle: asset.bundle,
        url,
        clips: asset.animations,
      });
    }
  }

  registerObjects(definitions);
  registerRenderers(renderers);
}
